import { useState, useEffect } from "react";
import { visit } from "@hotwired/turbo";
import { useApiRequest } from "../../hooks/useApiRequest";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { ChevronDown, Check } from "lucide-react";
import { formatInTimeZone } from "date-fns-tz";
import { ExpiringLink, Avatar, Brain } from "../../types/expiring_links.d";

interface ExpiringLinkCreateProps {
  expiringLink: ExpiringLink;
  avatars: Avatar[];
}

// #STYLE_GUIDE: Create component for new record creation
// - Uses daisyUI components for consistent styling
// - Navigation via Turbo.visit() for SPA-like experience
// - API calls via useApiRequest hook for consistency
// #TODO: This is a basic implementation - customize form fields and validation as needed
export default function ExpiringLinkCreate({
  expiringLink,
  avatars,
}: ExpiringLinkCreateProps) {
  const [timezone] = useState(() => {
    // Detect user's timezone
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  });

  const [formData, setFormData] = useState({
    slug: "",
    avatarId: "",
    expiresAt: "",
    timezone: timezone,
    brainIds: [] as string[],
  });

  const [selectedAvatar, setSelectedAvatar] = useState<Avatar | null>(null);
  const [avatarQuery, setAvatarQuery] = useState("");
  const [availableBrains, setAvailableBrains] = useState<Brain[]>([]);
  const [loadingBrains, setLoadingBrains] = useState(false);

  const { loading, makeRequest, getFieldError } = useApiRequest({
    onSuccess: (data) => {
      // #TURBO: Use Turbo.visit for navigation instead of window.location
      visit(`/expiring_links/${data.id}`);
    },
    onError: (error) => {
      console.error("Failed to create expiring_link:", error);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      avatarId: selectedAvatar?.id || "",
    };

    await makeRequest("POST", "/api/v1/expiring_links", {
      expiringLink: submitData,
    });
  };

  // Fetch brains when avatar is selected
  useEffect(() => {
    if (selectedAvatar?.id) {
      setLoadingBrains(true);
      fetch(`/api/v1/brains?avatar_id=${selectedAvatar.id}`)
        .then((res) => res.json())
        .then((data) => {
          setAvailableBrains(data);
          setLoadingBrains(false);
        })
        .catch((error) => {
          console.error("Failed to fetch brains:", error);
          setLoadingBrains(false);
        });
    } else {
      setAvailableBrains([]);
      setFormData((prev) => ({ ...prev, brainIds: [] }));
    }
  }, [selectedAvatar]);

  const toggleBrain = (brainId: string) => {
    setFormData((prev) => ({
      ...prev,
      brainIds: prev.brainIds.includes(brainId)
        ? prev.brainIds.filter((id) => id !== brainId)
        : [...prev.brainIds, brainId],
    }));
  };

  const handleCancel = () => {
    // #TURBO: Use Turbo.visit for navigation
    visit("/expiring_links");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Create New ExpiringLink</h1>
        <button onClick={handleCancel} className="btn btn-outline">
          Cancel
        </button>
      </div>

      {/* Form */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Slug field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Slug</span>
              </label>
              <input
                type="text"
                className={`input input-bordered ${
                  getFieldError("slug") ? "input-error" : ""
                }`}
                placeholder="Enter slug"
                value={formData.slug}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    slug: e.target.value,
                  }))
                }
              />
              {getFieldError("slug") && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {getFieldError("slug")}
                  </span>
                </label>
              )}
            </div>

            {/* Avatar field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Avatar</span>
              </label>
              <div className="relative">
                <Listbox
                  value={selectedAvatar}
                  onChange={(value) => {
                    setSelectedAvatar(value);
                    setFormData((prev) => ({
                      ...prev,
                      avatarId: value?.id || "",
                    }));
                  }}
                >
                  <ListboxButton
                    className={`input input-bordered w-full text-left ${
                      getFieldError("avatarId") ? "input-error" : ""
                    }`}
                  >
                    {selectedAvatar?.displayName || "Select avatar"}
                    <ChevronDown className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none w-5 h-5" />
                  </ListboxButton>
                  <ListboxOptions className="absolute z-10 mt-1 w-full bg-base-100 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {(() => {
                      const filteredAvatar = avatars.filter((item) =>
                        item.displayName
                          .toLowerCase()
                          .includes(avatarQuery.toLowerCase())
                      );

                      if (filteredAvatar.length === 0) {
                        return (
                          <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                            No avatar found.
                          </div>
                        );
                      }

                      return filteredAvatar.map((item) => (
                        <ListboxOption
                          key={item.id}
                          value={item}
                          className="relative cursor-default select-none py-2 pl-10 pr-4 hover:bg-base-200"
                        >
                          {item.displayName}
                          {selectedAvatar?.id === item.id && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                              <Check className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </ListboxOption>
                      ));
                    })()}
                  </ListboxOptions>
                </Listbox>
              </div>
              {getFieldError("avatarId") && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {getFieldError("avatarId")}
                  </span>
                </label>
              )}
            </div>

            {/* Expires at field */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">Expires at</span>
              </label>
              <input
                type="datetime-local"
                className={`input input-bordered ${
                  getFieldError("expiresAt") ? "input-error" : ""
                }`}
                placeholder="Enter expires at"
                value={formData.expiresAt}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    expiresAt: e.target.value,
                  }))
                }
              />
              {formData.expiresAt && (
                <label className="label">
                  <span className="label-text-alt">
                    Setting expiration in{" "}
                    {formatInTimeZone(
                      new Date(formData.expiresAt),
                      timezone,
                      "zzz"
                    )}{" "}
                    ({timezone})
                  </span>
                </label>
              )}
              {getFieldError("expiresAt") && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {getFieldError("expiresAt")}
                  </span>
                </label>
              )}
            </div>

            {/* Brains selection */}
            {selectedAvatar && (
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Select Brains</span>
                  <span className="label-text-alt">
                    {formData.brainIds.length} selected
                  </span>
                </label>
                {loadingBrains ? (
                  <div className="flex justify-center p-4">
                    <span className="loading loading-spinner loading-md"></span>
                  </div>
                ) : availableBrains.length === 0 ? (
                  <div className="alert alert-info">
                    <span>No brains available for this avatar</span>
                  </div>
                ) : (
                  <div className="border border-base-300 rounded-lg p-4 space-y-2 max-h-60 overflow-y-auto">
                    {availableBrains.map((brain) => (
                      <label
                        key={brain.id}
                        className="flex items-center gap-3 p-2 hover:bg-base-200 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary"
                          checked={formData.brainIds.includes(brain.id)}
                          onChange={() => toggleBrain(brain.id)}
                        />
                        <span className="flex-1">{brain.displayName}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Submit buttons */}
            <div className="form-control mt-6">
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Creating...
                    </>
                  ) : (
                    "Create ExpiringLink"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-outline"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
